import React, { createContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useLazyQuery } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import { useToast } from "hooks";
import {
  BLUEPRINT_GENERATION_CONFIG,
  hasBlueprintGenerationEnded,
  isBlueprintGenerating,
  isBlueprintGenerationCompleted,
  isBlueprintGenerationFailed,
} from "constants/index";

const BlueprintGenerationTasksContext = createContext(null);

/**
 * BlueprintGenerationTasksProvider
 *
 * Reads blueprints from a query and re-reads it while any of them is being generated, toasting each one that finishes.
 *
 * @param {node}      children      Components that consume the blueprints
 * @param {Object}    query         The blueprint query to read and poll
 * @param {Object}    variables     Its variables
 * @param {Function}  getTemplates  Picks the blueprints out of the query's data
 * @param {Boolean}   skip          Holds off querying until the caller's variables are settled
 * @param {Function}  onFinished    Called with each blueprint whose generation just ended
 * @param {Object}    config        Polling configuration
 */
const BlueprintGenerationTasksProvider = ({
  children,
  query,
  variables,
  getTemplates,
  skip = false,
  onFinished,
  config = BLUEPRINT_GENERATION_CONFIG,
}) => {
  const { toast } = useToast();
  const [initialized, setInitialized] = useState(false);
  const pollingIntervalRef = useRef(null);
  const announcedRef = useRef(new Set());
  const removedIdsRef = useRef(new Set());
  const fetchRef = useRef();
  const variablesKey = JSON.stringify(variables || {});

  const [getData, { data, loading: fetching, error }] = useLazyQuery(query, { fetchPolicy: "network-only" });

  const templates = useMemo(
    () => (getTemplates(data) || []).filter(({ id }) => !removedIdsRef.current.has(id)),
    [data]
  );
  const generating = useMemo(() => templates.some(isBlueprintGenerating), [templates]);

  fetchRef.current = () =>
    getData({ variables }).catch((fetchError) =>
      Sentry.captureException(fetchError, { tags: { feature: "blueprint_generation", action: "fetch_templates" } })
    );

  /* Initial load, once the caller's variables are settled */
  useEffect(() => {
    if (skip) return;

    fetchRef.current();
  }, [skip, variablesKey]);

  /* Poll only while something is still being generated */
  useEffect(() => {
    if (skip || !generating) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;

      return undefined;
    }

    if (pollingIntervalRef.current) return undefined;

    pollingIntervalRef.current = setInterval(() => fetchRef.current(), config.POLLING_INTERVAL);

    return undefined;
  }, [skip, generating, config.POLLING_INTERVAL]);

  /* Cleanup on unmount */
  useEffect(
    () => () => {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!data) return;

    notifyNewlyFinished(templates, { initialized, toast, announcedRef, onFinished });
    setInitialized(true);
  }, [data]);

  /**
   * Keeps a removed blueprint out of the list even if a poll already in flight still returns it.
   */
  const excludeTemplate = (id) => removedIdsRef.current.add(id);

  const value = useMemo(
    () => ({
      templates,
      initialized,
      loading: skip || (!initialized && fetching),
      error,
      generating,
      excludeTemplate,
      refetchTemplates: () => fetchRef.current(),
    }),
    [templates, initialized, skip, fetching, error, generating]
  );

  return <BlueprintGenerationTasksContext.Provider value={value}>{children}</BlueprintGenerationTasksContext.Provider>;
};

/**
 * Toasts once for each blueprint whose generation has just ended, as ready or failed.
 * Skipped on the first load, where a finished generation only means "finished before you arrived".
 */
const notifyNewlyFinished = (templates, { initialized, toast, announcedRef, onFinished }) => {
  const finished = templates.filter(hasBlueprintGenerationEnded);

  if (!initialized) {
    finished.forEach(({ id }) => announcedRef.current.add(id));

    return;
  }

  finished.forEach((template) => {
    if (announcedRef.current.has(template.id)) return;

    announcedRef.current.add(template.id);

    if (isBlueprintGenerationCompleted(template)) {
      toast.success(<FormattedMessage id="Blueprints.Generation.Completed" values={{ name: template.name }} />);
    } else if (isBlueprintGenerationFailed(template)) {
      toast.error(<FormattedMessage id="Blueprints.Generation.Failed" values={{ name: template.name }} />);
    }

    onFinished?.(template);
  });
};

BlueprintGenerationTasksProvider.propTypes = {
  children: PropTypes.node.isRequired,
  query: PropTypes.object.isRequired,
  variables: PropTypes.object,
  getTemplates: PropTypes.func.isRequired,
  skip: PropTypes.bool,
  onFinished: PropTypes.func,
  config: PropTypes.shape({
    POLLING_INTERVAL: PropTypes.number,
  }),
};

export default BlueprintGenerationTasksProvider;

export { BlueprintGenerationTasksContext };
